import React, { useEffect, useState, useMemo } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { useAuthz } from "../../auth/useAuthz";
import {
  ADMIN_INVENTORY_DASHBOARD,
  EMPLOYEE_LIST,
  GET_INVENTORY,
  ASSIGN_INVENTORY_TO_EMPLOYEE,
  GET_INVENTORY_HISTORY,
} from "./graphql";
import PendingInventoryDrawer from "./PendingInventoryDrawer";
import PurchaseHistoryModal from "./PurchaseHistoryModal";
import InventoryHistoryModal from "./InventoryHistoryModal";
import InventoryReconciliationReport from "./InventoryReconciliationReport";
import InventoryUsageDashboard from "./InventoryUsageDashboard";
import ProductSelectionGaps from "./ProductSelectionGaps";
import InventoryTraceability from "./InventoryTraceability";
import RecipeDriftSuggestions from "./RecipeDriftSuggestions";
import WeeklyAuditQueue from "./WeeklyAuditQueue";
import { FiMoreVertical } from "react-icons/fi";

function getStockStatusClasses(status) {
  switch (status) {
    case "OUT_OF_STOCK":
      return "bg-red-100 text-red-800";
    case "LOW_STOCK":
      return "bg-yellow-100 text-yellow-800";
    case "IN_STOCK":
    default:
      return "bg-green-100 text-green-800";
  }
}

function getStockStatusLabel(status) {
  switch (status) {
    case "OUT_OF_STOCK":
      return "Out of Stock";
    case "LOW_STOCK":
      return "Low Stock";
    case "IN_STOCK":
    default:
      return "In Stock";
  }
}

const AdminInventoryDashboard = () => {
  const { can } = useAuthz();
  const [search, setSearch] = useState("");
  const [stockFilter, setStockFilter] = useState("ALL"); // ALL | IN_STOCK | LOW_STOCK | OUT_OF_STOCK
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("ALL");
  const [employees, setEmployees] = useState([]);
  const [viewMode, setViewMode] = useState("OVERVIEW"); // OVERVIEW | HISTORY | RECONCILIATION
  const [expandedDates, setExpandedDates] = useState({});
  const [drawerDate, setDrawerDate] = useState(null);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const isEmployeeView = selectedEmployeeId !== "ALL";

  // 🔹 Assign modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignMode, setAssignMode] = useState("BY_INVENTORY"); // "BY_INVENTORY" | "BY_CATEGORY"
  const [assignCategory, setAssignCategory] = useState(null); // selected category object

  const [assignEmployeeId, setAssignEmployeeId] = useState("ALL");
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [inventorySearchResults, setInventorySearchResults] = useState([]);
  const [selectedInventories, setSelectedInventories] = useState([]); // [{id, code}]
  const [quantityToAssign, setQuantityToAssign] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [assignError, setAssignError] = useState("");

  // 🔹 Pending Inventory Drawer state
  const [pendingDrawerOpen, setPendingDrawerOpen] = useState(false);
  const [pendingDrawerCategory, setPendingDrawerCategory] = useState(null); // { id, name }
  const [drawerStatus, setDrawerStatus] = useState("Pending");

  // 🔹 Purchase History Modal state
  const [historyModalCategory, setHistoryModalCategory] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null); // track which category's dropdown is open

  // 🔹 Inventory History Modal state
  const [historySearchInput, setHistorySearchInput] = useState("");
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const [fetchHistory, { data: historyDataRes, loading: historyLoading }] = useLazyQuery(GET_INVENTORY_HISTORY, {
    fetchPolicy: "network-only",
  });

  const handleSearchHistory = (e) => {
    e.preventDefault();
    if (!historySearchInput.trim()) return;
    setHistoryModalOpen(true);
    fetchHistory({
      variables: { inventoryId: historySearchInput.trim() },
    });
  };

  // Employees for filter
  const {
    data: employeesData,
    loading: employeesLoading,
    error: employeesError,
  } = useQuery(EMPLOYEE_LIST, {
    variables: {
      data: {
        bookableOrEmployed: true,
      },
    },
  });

  useEffect(() => {
    if (employeesData?.getEmployeelist?.employee) {
      const empList = employeesData.getEmployeelist.employee.map((emp) => ({
        id: emp.id,
        name: emp.fullName,
      }));
      setEmployees(empList);
    }
  }, [employeesData]);

  // Dashboard data, dependent on selectedEmployeeId
  const { data, loading, error, refetch } = useQuery(
    ADMIN_INVENTORY_DASHBOARD,
    {
      variables: {
        employeeId: selectedEmployeeId === "ALL" ? null : selectedEmployeeId,
      },
      fetchPolicy: "network-only",
    }
  );

  const response = data?.getAdminInventoryDashboard;
  const summary = response?.data?.summary;
  const categories = response?.data?.categories || [];
  const historyByDate = response?.data?.historyByDate || [];

  const filteredHistory = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return historyByDate;

    return historyByDate
      .map((dateObj) => {
        const filteredList = dateObj.inventoryList.filter((cat) =>
          cat.catName.toLowerCase().includes(term)
        );
        return {
          ...dateObj,
          inventoryList: filteredList,
        };
      })
      .filter((dateObj) => dateObj.inventoryList.length > 0);
  }, [historyByDate, search]);

  const toggleDate = (date) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  // 1️⃣ Filter categories based on search + stock filter
  const filteredCategories = useMemo(() => {
    const term = search.toLowerCase();

    return categories.filter((cat) => {
      const matchesSearch =
        cat.name.toLowerCase().includes(term) ||
        (cat.abbr && cat.abbr.toLowerCase().includes(term));

      const matchesStock =
        stockFilter === "ALL" ? true : cat.stockStatus === stockFilter;

      return matchesSearch && matchesStock;
    });
  }, [categories, search, stockFilter]);

  // 2️⃣ Sorting helper
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedCategories = useMemo(() => {
    if (!sortConfig.key) return filteredCategories;

    const sorted = [...filteredCategories].sort((a, b) => {
      const { key } = sortConfig;
      const dir = sortConfig.direction === "asc" ? 1 : -1;

      const getValue = (cat) => {
        switch (key) {
          case "name":
            return cat.name?.toLowerCase() || "";
          case "usableQuantity":
            return cat.usableQuantity ?? 0;
          case "effectiveMinStock":
            return isEmployeeView ? cat.minStock ?? 0 : cat.minStockAdmin ?? 0;
          case "stockStatus":
            return cat.stockStatus || "";
          case "totalItems":
            return cat.totalItems ?? 0;
          case "utilizationRate":
            return cat.utilizationRate ?? 0;
          case "assigned":
            return cat.assigned ?? 0;
          case "staleUnassigned":
            return cat.staleUnassigned ?? 0;
          default:
            return 0;
        }
      };

      const aVal = getValue(a);
      const bVal = getValue(b);

      if (typeof aVal === "string" && typeof bVal === "string") {
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      }

      return (aVal - bVal) * dir;
    });

    return sorted;
  }, [filteredCategories, sortConfig, isEmployeeView]);

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const totalUsableLabel = isEmployeeView
    ? "Total Usable Parts"
    : "Total Usable Quantity";

  const minStockHeaderLabel = isEmployeeView
    ? "Min Stock (Parts)"
    : "Min Stock (Items)";

  // 🔹 Inventory search via GraphQL (getInventory)
  const [searchInventory, { loading: invLoading, error: invError }] =
    useLazyQuery(GET_INVENTORY, {
      onCompleted: (data) => {
        const list = data?.getInventory?.inventoryList || [];
        // Map GraphQL Inventory -> shape used in UI
        const mapped = list.map((inv) => ({
          id: inv.id,
          // Treat Inventory.name as the "inventory code" to show/search
          code: inv.name,
          meta:
            inv.description ||
            inv.category ||
            inv.status ||
            inv.employeeName ||
            "",
        }));
        setInventorySearchResults(mapped);
      },
    });

  // 🔹 Mutation: assignInventoryToEmployee (IDs + category bulk)
  const [assignInventoryToEmployee, { loading: assignLoading }] = useMutation(
    ASSIGN_INVENTORY_TO_EMPLOYEE
  );

  // Trigger search when term changes
  useEffect(() => {
    if (
      !assignCategory ||
      !inventorySearchTerm ||
      inventorySearchTerm.length < 2
    ) {
      setInventorySearchResults([]);
      return;
    }

    // Call GraphQL getInventory
    searchInventory({
      variables: {
        data: {
          search: inventorySearchTerm,
          categoryId: assignCategory.id,
          // Adjust based on your backend statuses
          status: "UNASSIGNED",
          onlyUsable: true,
          // employeeId: null
        },
      },
    });
  }, [inventorySearchTerm, assignCategory, searchInventory]);

  const handleAddInventory = (inv) => {
    setSelectedInventories((prev) => {
      if (prev.some((p) => p.id === inv.id)) return prev;
      return [...prev, inv];
    });
  };

  const handleRemoveInventory = (id) => {
    setSelectedInventories((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmitAssign = async () => {
    setAssignError("");

    if (!assignEmployeeId || assignEmployeeId === "ALL") {
      setAssignError("Please select an employee.");
      return;
    }

    if (!assignCategory) {
      setAssignError("Category is missing.");
      return;
    }

    try {
      setSubmitting(true);

      const dataInput = {
        employeeId: assignEmployeeId,
      };

      if (assignMode === "BY_INVENTORY") {
        if (selectedInventories.length === 0) {
          setAssignError("Select at least one inventory code.");
          setSubmitting(false);
          return;
        }

        dataInput.inventoryIds = selectedInventories.map((i) => i.id);
      } else if (assignMode === "BY_CATEGORY") {
        const qty = Number(quantityToAssign);
        if (!qty || qty <= 0) {
          setAssignError("Enter a valid quantity.");
          setSubmitting(false);
          return;
        }

        const maxQty =
          assignCategory.usableQuantity - assignCategory.pendingUsableQuantity ??
          Infinity;
        if (qty > maxQty) {
          setAssignError(
            `You cannot assign more than ${maxQty} pieces for this category.`
          );
          setSubmitting(false);
          return;
        }

        dataInput.categoryAssignments = [
          {
            categoryId: assignCategory.id,
            quantity: qty,
          },
        ];
      }

      // 🔹 Call mutation
      const res = await assignInventoryToEmployee({
        variables: {
          data: dataInput,
        },
      });

      const result = res?.data?.assignInventoryToEmployee;

      if (!result || result.status !== "SUCCESS") {
        setAssignError(result?.message || "Failed to assign inventory.");
        setSubmitting(false);
        return;
      }

      // Partly met / partly-used stock - assigned, but admin must know.
      if (result.warnings?.length) {
        window.alert(result.message);
      }

      // 🔹 Refetch dashboard with current Admin/Employee view
      await refetch({
        employeeId: selectedEmployeeId === "ALL" ? null : selectedEmployeeId,
      });

      // Reset + close modal
      setAssignModalOpen(false);
      setSelectedInventories([]);
      setQuantityToAssign("");
    } catch (e) {
      console.error(e);
      setAssignError(e.message || "Something went wrong while assigning.");
    } finally {
      setSubmitting(false);
    }
  };

  const isAssigning = submitting || assignLoading;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* PAGE HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Inventory Dashboard
            </h1>
            <p className="text-sm text-gray-500">
              Category-wise inventory status for admin
              {isEmployeeView ? " (per Employee)" : " (Global)"}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {response?.status === "SUCCESS" && (
              <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 border border-green-100">
                {response.message}
              </span>
            )}

            {response?.status === "ERROR" && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 border border-red-100">
                {response.message}
              </span>
            )}

            {/* Search Inventory Code */}
            <form onSubmit={handleSearchHistory} className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search inventory ID..."
                value={historySearchInput}
                onChange={(e) => setHistorySearchInput(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:outline-none"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-purple-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-purple-700 transition"
              >
                Find
              </button>
            </form>

            {can("inventory.create") && (
              <div>
                <Link
                  to="/admin/inventory/create"
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
                >
                  Add Inventory
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* FILTER ROW: Search + Stock Filter + Employee Filter */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between flex-wrap">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-80 px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          {/* Stock filter */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 text-xs sm:text-sm">
              Filter by stock:
            </span>
            <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden text-xs sm:text-sm">
              {["ALL", "IN_STOCK", "LOW_STOCK", "OUT_OF_STOCK"].map((value) => {
                const isActive = stockFilter === value;
                const labelMap = {
                  ALL: "All",
                  IN_STOCK: "In Stock",
                  LOW_STOCK: "Low Stock",
                  OUT_OF_STOCK: "Out of Stock",
                };
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setStockFilter(value)}
                    className={`px-3 py-1.5 whitespace-nowrap ${isActive
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {labelMap[value]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Employee filter (page-level) */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 text-xs sm:text-sm">Employee:</span>
            <select
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="ALL">Admin</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>

            {isEmployeeView && (
              <button
                type="button"
                onClick={() => {
                  setPendingDrawerCategory(null);
                  setDrawerDate(null);
                  setPendingDrawerOpen(true);
                }}
                className="px-3 py-2 bg-[#7F58E6] text-white rounded-xl text-xs sm:text-sm hover:bg-[#6c48c5] transition font-medium"
              >
                View Pending Items
              </button>
            )}
          </div>

          {/* View Mode toggle */}
          <div className="flex items-center gap-2 text-sm mt-4 md:mt-0">
            <span className="text-gray-500 text-xs sm:text-sm">View:</span>
            <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden text-xs sm:text-sm">
              {["OVERVIEW", "USAGE", "HISTORY", "RECONCILIATION", "PRODUCT_GAPS", "TRACEABILITY", "RECIPE_DRIFT", "AUDIT"].map((value) => {
                const isActive = viewMode === value;
                const labelMap = {
                  OVERVIEW: "Overview",
                  USAGE: "Usage Dashboard",
                  HISTORY: "History",
                  RECONCILIATION: "Reconciliation",
                  PRODUCT_GAPS: "Product Gaps",
                  TRACEABILITY: "Expiry & Recall",
                  RECIPE_DRIFT: "Recipe Drift",
                  AUDIT: "Weekly Audit",
                };
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setViewMode(value)}
                    className={`px-3 py-1.5 whitespace-nowrap ${isActive
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    {labelMap[value]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* LOADING */}
        {((!["USAGE", "RECONCILIATION", "PRODUCT_GAPS", "TRACEABILITY", "RECIPE_DRIFT", "AUDIT"].includes(viewMode) && loading) ||
          employeesLoading) && (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-600" />
            <span className="ml-3 text-gray-600 text-sm">Loading...</span>
          </div>
        )}

        {/* ERROR */}
        {((!["USAGE", "RECONCILIATION", "PRODUCT_GAPS", "TRACEABILITY", "RECIPE_DRIFT", "AUDIT"].includes(viewMode) && error) ||
          employeesError) && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-4">
            {error?.message || employeesError?.message}
          </div>
        )}

        {/* USAGE DASHBOARD VIEW */}
        {viewMode === "USAGE" && !employeesLoading && !employeesError && (
          <InventoryUsageDashboard employees={employees} />
        )}

        {/* RECONCILIATION VIEW */}
        {viewMode === "RECONCILIATION" && !employeesLoading && !employeesError && (
          <InventoryReconciliationReport employees={employees} />
        )}

        {/* PRODUCT SELECTION GAPS VIEW */}
        {viewMode === "PRODUCT_GAPS" && !employeesLoading && !employeesError && (
          <ProductSelectionGaps employees={employees} />
        )}

        {/* EXPIRY & RECALL TRACEABILITY VIEW */}
        {viewMode === "TRACEABILITY" && <InventoryTraceability />}

        {/* RECIPE DRIFT SUGGESTIONS VIEW */}
        {viewMode === "RECIPE_DRIFT" && <RecipeDriftSuggestions />}

        {/* WEEKLY BLIND AUDIT VIEW */}
        {viewMode === "AUDIT" && <WeeklyAuditQueue />}

        {/* CONTENT */}
        {!["USAGE", "RECONCILIATION", "PRODUCT_GAPS", "TRACEABILITY", "RECIPE_DRIFT", "AUDIT"].includes(viewMode) &&
          !loading &&
          !error &&
          response?.data && (
          <>
            {viewMode === "OVERVIEW" ? (
              <>
                {/* SUMMARY CARDS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <p className="text-xs font-medium text-gray-500">
                  Total Categories
                </p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {summary.totalCategories}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <p className="text-xs font-medium text-gray-500">
                  {totalUsableLabel}
                </p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">
                  {isEmployeeView
                    ? summary.totalUsableParts
                    : summary.totalUsableQuantity}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <p className="text-xs font-medium text-gray-500">
                  Low Stock Categories
                </p>
                <p className="mt-2 text-2xl font-semibold text-amber-600">
                  {summary.lowStockCategories}
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
                <p className="text-xs font-medium text-gray-500">
                  Out of Stock Categories
                </p>
                <p className="mt-2 text-2xl font-semibold text-red-600">
                  {summary.outOfStockCategories}
                </p>
              </div>
            </div>

            {/* CATEGORY TABLE */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Category Inventory
                </h2>
                <p className="text-xs text-gray-500">
                  Showing {sortedCategories.length} of {categories.length}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th
                        className="px-4 py-3 cursor-pointer select-none"
                        onClick={() => handleSort("name")}
                      >
                        Category{renderSortIndicator("name")}
                      </th>
                      <th
                        className="px-4 py-3 cursor-pointer select-none"
                        onClick={() => handleSort("usableQuantity")}
                      >
                        {selectedEmployeeId === "ALL"
                          ? "In Store"
                          : "Usable Qty"}{" "}
                        {renderSortIndicator("usableQuantity")}
                      </th>
                      <th
                        className="px-4 py-3 cursor-pointer select-none"
                        onClick={() => handleSort("effectiveMinStock")}
                      >
                        {minStockHeaderLabel}
                        {renderSortIndicator("effectiveMinStock")}
                      </th>
                      <th
                        className="px-4 py-3 cursor-pointer select-none"
                        onClick={() => handleSort("stockStatus")}
                      >
                        Status{renderSortIndicator("stockStatus")}
                      </th>

                      <th
                        className="px-4 py-3 cursor-pointer select-none"
                        onClick={() => handleSort("assigned")}
                      >
                        Approval Pending{renderSortIndicator("assigned")}
                      </th>

                      {!isEmployeeView && (
                        <th className="px-4 py-3 cursor-pointer select-none">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {sortedCategories.map((cat, index) => {
                      const isLastFew = index >= sortedCategories.length - 2 && sortedCategories.length > 2;
                      const effectiveMinStock = isEmployeeView
                        ? cat.minStock ?? 0
                        : cat.minStockAdmin ?? 0;

                      return (
                        <tr key={cat.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{cat.name}</p>
                              {cat.abbr && (
                                <p className="text-xs text-gray-500">
                                  {cat.abbr}
                                </p>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            {(selectedEmployeeId === "ALL" ? cat.usableQuantity - (cat.pendingUsableQuantity || 0) : cat.usableQuantity) > 0 ? (
                              <button
                                onClick={() => {
                                  setPendingDrawerCategory(cat);
                                  setDrawerStatus(selectedEmployeeId === "ALL" ? "UNASSIGNED" : "Assigned");
                                  setDrawerDate(null);
                                  setPendingDrawerOpen(true);
                                }}
                                className="text-blue-600 hover:underline font-medium"
                              >
                                {selectedEmployeeId === "ALL" ? cat.usableQuantity - (cat.pendingUsableQuantity || 0) : cat.usableQuantity}
                              </button>
                            ) : (
                              0
                            )}
                            {(!cat.isScanRequired && (cat.parts ?? 0) > 0) && (
                              <p className="text-[11px] text-gray-400">
                                Parts available: {selectedEmployeeId == "ALL" ? (cat.usableParts ?? 0) - (cat.pendingUsableParts ?? 0) : cat.usableParts}
                              </p>
                            )}
                          </td>

                          <td className="px-4 py-3">{effectiveMinStock}</td>

                          <td className="px-4 py-3">
                            <span
                              className={`px-3 py-1 rounded-full text-xs ${getStockStatusClasses(
                                cat.stockStatus
                              )}`}
                            >
                              {getStockStatusLabel(cat.stockStatus)}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            {cat.pendingUsableQuantity > 0 ? (
                              <button
                                onClick={() => {
                                  setPendingDrawerCategory(cat);
                                  setDrawerStatus("Pending");
                                  setDrawerDate(null);
                                  setPendingDrawerOpen(true);
                                }}
                                className="text-blue-600 hover:underline font-medium"
                              >
                                {cat.pendingUsableQuantity}
                              </button>
                            ) : (
                              cat.pendingUsableQuantity
                            )}
                            <p className="text-[11px] text-gray-400">
                              Parts available: {cat.pendingUsableParts ?? 0}
                            </p>
                          </td>

                          {!isEmployeeView && (
                            <td className="px-4 py-3 relative">
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="text-blue-600 text-sm font-medium hover:underline"
                                  onClick={() => {
                                    setAssignCategory(cat);
                                    setAssignMode("BY_CATEGORY");

                                    setAssignEmployeeId(
                                      selectedEmployeeId === "ALL"
                                        ? "ALL"
                                        : selectedEmployeeId
                                    );

                                    setInventorySearchTerm("");
                                    setInventorySearchResults([]);
                                    setSelectedInventories([]);
                                    setQuantityToAssign("");
                                    setAssignError("");

                                    setAssignModalOpen(true);
                                  }}
                                >
                                  Assign
                                </button>
                                
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenDropdownId(openDropdownId === cat.id ? null : cat.id);
                                    }}
                                    className="p-1 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                                  >
                                    <FiMoreVertical size={18} />
                                  </button>
                                  {openDropdownId === cat.id && (
                                    <>
                                      {/* Invisible overlay to close dropdown on outside click */}
                                      <div 
                                        className="fixed inset-0 z-40"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenDropdownId(null);
                                        }}
                                      ></div>
                                      <div className={`absolute right-0 ${isLastFew ? 'bottom-full mb-1' : 'top-full mt-1'} w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1`}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setHistoryModalCategory(cat);
                                            setOpenDropdownId(null);
                                          }}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                                        >
                                          Purchase History
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {sortedCategories.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-6 text-center text-sm text-gray-500"
                        >
                          No categories found for current search / filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            </>
            ) : (
              <div className="space-y-4">
                {filteredHistory.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    No history found matching the filters.
                  </div>
                ) : (
                  filteredHistory.map((dateObj) => (
                    <div key={dateObj.date} className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleDate(dateObj.date)}
                        className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <h3 className="font-semibold text-gray-900 text-sm">
                          {new Date(dateObj.date).toLocaleDateString("en-US", {
                            weekday: "short",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </h3>
                        <div className="flex items-center gap-3">
                          <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full">
                            {dateObj.inventoryList.reduce((acc, cat) => acc + cat.count, 0)} Items
                          </span>
                          <span className="text-gray-400 text-xs">
                            {expandedDates[dateObj.date] ? "▲" : "▼"}
                          </span>
                        </div>
                      </button>

                      {expandedDates[dateObj.date] && (
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-100 bg-white">
                          {dateObj.inventoryList.map((cat) => (
                            <div
                              key={cat.categoryId}
                              onClick={() => {
                                setPendingDrawerCategory({ id: cat.categoryId, name: cat.catName });
                                setDrawerStatus(null);
                                setDrawerDate(dateObj.date);
                                setPendingDrawerOpen(true);
                              }}
                              className="border border-gray-200 rounded-xl p-4 flex flex-col gap-2 bg-white cursor-pointer hover:border-blue-300 hover:shadow-md transition"
                            >
                              <div className="flex justify-between items-start">
                                <p className="font-semibold text-gray-900 text-sm">{cat.catName}</p>
                                <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-lg">
                                  Qty: {cat.count}
                                </span>
                              </div>
                              {cat.verifiedDate && (
                                <p className="text-[11px] text-green-600 bg-green-50 px-2 py-1 rounded w-fit mt-1">
                                  Verified: {new Date(cat.verifiedDate).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ASSIGN MODAL */}
      {assignModalOpen && assignCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 relative">
            {/* Optional overlay while assigning */}
            {isAssigning && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-2xl">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-600" />
                  <span>Assigning...</span>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Assign Inventory
                </h3>
                <p className="text-xs text-gray-500">
                  Category:{" "}
                  <span className="font-medium">{assignCategory.name}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => !isAssigning && setAssignModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                disabled={isAssigning}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Employee select */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Assign to Employee
                </label>
                <select
                  value={assignEmployeeId}
                  onChange={(e) => setAssignEmployeeId(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  disabled={isAssigning}
                >
                  <option value="ALL" disabled>
                    Select employee
                  </option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mode toggle */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Assignment Type
                </p>
                <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1 text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      !isAssigning && setAssignMode("BY_INVENTORY")
                    }
                    className={`px-3 py-1.5 rounded-lg ${assignMode === "BY_INVENTORY"
                      ? "bg-white shadow-sm text-blue-600"
                      : "text-gray-600"
                      }`}
                    disabled={isAssigning}
                  >
                    By Inventory Code
                  </button>

                  <button
                    type="button"
                    disabled={isAssigning}
                    onClick={() => setAssignMode("BY_CATEGORY")}
                    className={`px-3 py-1.5 rounded-lg ml-1 ${assignMode === "BY_CATEGORY"
                      ? "bg-white shadow-sm text-blue-600"
                      : "text-gray-600"
                      }`}
                  >
                    By Category (bulk)
                  </button>
                </div>
              </div>

              {/* BY INVENTORY MODE */}
              {assignMode === "BY_INVENTORY" && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Search Inventory Codes
                    </label>
                    <input
                      type="text"
                      value={inventorySearchTerm}
                      onChange={(e) => setInventorySearchTerm(e.target.value)}
                      placeholder="Type inventory code…"
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      disabled={isAssigning}
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Start typing to search inventory items for this category.
                    </p>
                  </div>

                  {invLoading && (
                    <p className="text-[11px] text-gray-400">
                      Searching inventory…
                    </p>
                  )}
                  {invError && (
                    <p className="text-[11px] text-red-500">
                      Error while searching inventory.
                    </p>
                  )}

                  {/* Search results */}
                  {inventorySearchResults.length > 0 && (
                    <div className="border border-gray-100 rounded-xl max-h-40 overflow-y-auto text-xs">
                      {inventorySearchResults.map((inv) => (
                        <button
                          key={inv.id}
                          type="button"
                          onClick={() => handleAddInventory(inv)}
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left"
                          disabled={isAssigning}
                        >
                          <div>
                            <p className="font-medium">{inv.code}</p>
                            {inv.meta && (
                              <p className="text-[11px] text-gray-400">
                                {inv.meta}
                              </p>
                            )}
                          </div>
                          <span className="text-blue-600 text-[11px]">Add</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Selected list */}
                  {selectedInventories.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-1">
                        Selected Inventory ({selectedInventories.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedInventories.map((inv) => (
                          <span
                            key={inv.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-xs text-blue-700"
                          >
                            {inv.code}
                            <button
                              type="button"
                              onClick={() => handleRemoveInventory(inv.id)}
                              className="text-[11px] text-blue-500 hover:text-blue-700"
                              disabled={isAssigning}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* BY CATEGORY MODE */}
              {assignMode === "BY_CATEGORY" && (
                <div className="space-y-3">
                  <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                    <p>
                      You are assigning by <b>category</b> (no codes).
                    </p>
                    <p>
                      Available usable quantity:{" "}
                      <span className="font-semibold">
                        {assignCategory.usableQuantity -
                          assignCategory.pendingUsableQuantity}
                      </span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Quantity to assign
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={quantityToAssign}
                      onChange={(e) => setQuantityToAssign(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g. 30"
                      disabled={isAssigning}
                    />
                    <p className="mt-1 text-[11px] text-gray-400">
                      Pieces to assign to the selected employee.
                    </p>
                  </div>
                </div>
              )}

              {/* Error */}
              {assignError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                  {assignError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => !isAssigning && setAssignModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitAssign}
                className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                disabled={isAssigning}
              >
                {isAssigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Pending Inventory Drawer */}
      <PendingInventoryDrawer
        isOpen={pendingDrawerOpen}
        onClose={() => setPendingDrawerOpen(false)}
        categoryId={pendingDrawerCategory?.id}
        categoryName={pendingDrawerCategory?.name}
        employeeId={drawerStatus === "Unassigned" ? "ALL" : selectedEmployeeId}
        status={drawerStatus}
        date={drawerDate}
        onlyUsable={true}
      />

      {/* PURCHASE HISTORY MODAL */}
      {historyModalCategory && (
        <PurchaseHistoryModal
        isOpen={!!historyModalCategory}
        onClose={() => setHistoryModalCategory(null)}
        category={historyModalCategory}
      />
      )}

      <InventoryHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        data={historyDataRes?.getInventoryHistory?.data}
        loading={historyLoading}
      />
    </div>
  );
};

export default AdminInventoryDashboard;
